import { Head, router, usePage } from "@inertiajs/react"
import { Check, Plus } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

import { CreateListModal } from "@/components/CreateListModal"
import { DashboardTaskItem } from "@/components/DashboardTaskItem"
import { DeleteConfirmationModal } from "@/components/DeleteConfirmationModal"
import { EditTaskModal } from "@/components/EditTaskModal"
import EmptyListsState from "@/components/EmptyListsState"
import { KeyboardShortcutsModal } from "@/components/KeyboardShortcutsModal"
import { LimitReachedModal } from "@/components/LimitReachedModal"
import { NewTaskModal } from "@/components/NewTaskModal"
import { VitalCard } from "@/components/VitalCard"
import { Button } from "@/components/ui/button"
import { useAllTasksUpdates } from "@/hooks/use-cable"
import {
  type KeyboardShortcut,
  useKeyboardShortcuts,
} from "@/hooks/use-keyboard-shortcuts"
import AppLayout from "@/layouts/app-layout"
import { flushListCache, flushListsIndexCache } from "@/lib/cache"
import { cn } from "@/lib/utils"
import { listPath, listTaskCompletionPath, listTaskPath } from "@/routes"
import type { AggregateTask, List, Vitals } from "@/types"

interface ListSummary {
  id: number
  name: string
}

interface ListsIndexProps {
  vitals: Vitals
  activeTasks: AggregateTask[]
  completedTasks: AggregateTask[]
  archivedTasks: AggregateTask[]
  totalCompleted: number
  totalArchived: number
  allLists: ListSummary[]
}

function formatCompletedDate(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return `Done ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
}

function formatArchivedDate(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

export default function ListsIndex({
  vitals,
  activeTasks,
  completedTasks,
  archivedTasks,
  allLists,
}: ListsIndexProps) {
  const [showNewListModal, setShowNewListModal] = useState(false)
  const [showNewTaskModal, setShowNewTaskModal] = useState(false)
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [showListLimitModal, setShowListLimitModal] = useState(false)
  const [showDeleteTaskModal, setShowDeleteTaskModal] =
    useState<AggregateTask | null>(null)
  const [editingTask, setEditingTask] = useState<AggregateTask | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const { lists = [] } = usePage().props as { lists?: List[] }

  const MAX_LISTS = 9
  const isAtListLimit = lists.length >= MAX_LISTS

  const handleNewListClick = useCallback(() => {
    if (isAtListLimit) {
      setShowListLimitModal(true)
    } else {
      setShowNewListModal(true)
    }
  }, [isAtListLimit])

  useAllTasksUpdates()

  const handleToggleComplete = useCallback((task: AggregateTask) => {
    const flushCache = () => {
      flushListCache(task.list.id)
      flushListsIndexCache()
    }

    if (task.status === "completed") {
      router.delete(listTaskCompletionPath(task.list.id, task.id), {
        preserveScroll: true,
        onSuccess: flushCache,
      })
    } else {
      router.post(
        listTaskCompletionPath(task.list.id, task.id),
        {},
        {
          preserveScroll: true,
          onSuccess: flushCache,
        },
      )
    }
  }, [])

  const navigableTasks = useMemo(
    () => [...activeTasks, ...completedTasks],
    [activeTasks, completedTasks],
  )

  const selectedTask = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < navigableTasks.length) {
      return navigableTasks[selectedIndex]
    }
    return null
  }, [selectedIndex, navigableTasks])

  const jumpToList = useCallback(
    (index: number) => {
      if (index < lists.length) {
        router.visit(listPath(lists[index].id))
      }
    },
    [lists],
  )

  const handleDeleteTask = useCallback((task: AggregateTask) => {
    router.delete(listTaskPath(task.list.id, task.id), {
      preserveScroll: true,
      onSuccess: () => {
        flushListCache(task.list.id)
        flushListsIndexCache()
        setShowDeleteTaskModal(null)
      },
    })
  }, [])

  const shortcuts: KeyboardShortcut[] = useMemo(
    () => [
      {
        key: "?",
        handler: () => setShowHelpModal(true),
        description: "Show keyboard shortcuts",
        category: "global",
      },
      {
        key: "n",
        handler: () => setShowNewTaskModal(true),
        description: "New task",
        category: "actions",
      },
      {
        key: "l",
        handler: handleNewListClick,
        description: "New list",
        category: "actions",
      },
      {
        key: "N",
        modifiers: { shift: true },
        handler: handleNewListClick,
        description: "New list",
        category: "actions",
      },
      {
        key: "k",
        handler: () => {
          if (navigableTasks.length === 0) return
          setSelectedIndex((prev) => {
            if (prev <= 0) return navigableTasks.length - 1
            return prev - 1
          })
        },
        description: "Previous task",
        category: "navigation",
        when: () => navigableTasks.length > 0,
      },
      {
        key: "j",
        handler: () => {
          if (navigableTasks.length === 0) return
          setSelectedIndex((prev) => {
            if (prev < 0 || prev >= navigableTasks.length - 1) return 0
            return prev + 1
          })
        },
        description: "Next task",
        category: "navigation",
        when: () => navigableTasks.length > 0,
      },
      {
        key: "x",
        handler: () => {
          if (selectedTask) {
            handleToggleComplete(selectedTask)
          }
        },
        description:
          selectedTask?.status === "completed"
            ? "Undo completion"
            : "Complete task",
        category: "actions",
        when: () => selectedTask !== null,
      },
      {
        key: "d",
        handler: () => {
          if (selectedTask) {
            setShowDeleteTaskModal(selectedTask)
          }
        },
        description: "Delete task",
        category: "actions",
        when: () => selectedTask !== null,
      },
      {
        key: "Enter",
        handler: () => {
          if (selectedTask) {
            setEditingTask(selectedTask)
          }
        },
        description: "Edit task",
        category: "actions",
        when: () => selectedTask !== null,
      },
      {
        key: "e",
        handler: () => {
          if (selectedTask) {
            setEditingTask(selectedTask)
          }
        },
        description: "Edit task",
        category: "actions",
        when: () => selectedTask !== null,
      },
      {
        key: "Escape",
        handler: () => setSelectedIndex(-1),
        description: "Clear selection",
        category: "navigation",
        when: () => selectedIndex >= 0,
      },
      ...Array.from({ length: 9 }, (_, i) => ({
        key: String(i + 1),
        handler: () => jumpToList(i),
        description: `Jump to list ${i + 1}`,
        category: "navigation" as const,
        when: () => lists.length > i,
      })),
    ],
    [
      navigableTasks.length,
      selectedTask,
      selectedIndex,
      handleToggleComplete,
      handleNewListClick,
      jumpToList,
      lists.length,
    ],
  )

  useKeyboardShortcuts({ shortcuts })

  const visibleShortcuts = useMemo((): KeyboardShortcut[] => {
    const baseShortcuts: KeyboardShortcut[] = [
      { key: "?", description: "Show keyboard shortcuts", category: "global" },
      { key: "j", description: "Next task", category: "navigation" },
      { key: "k", description: "Previous task", category: "navigation" },
      { key: "x", description: "Toggle complete", category: "actions" },
      { key: "d", description: "Delete task", category: "actions" },
      { key: "e", description: "Edit task", category: "actions" },
      { key: "Enter", description: "Edit task", category: "actions" },
      { key: "Escape", description: "Clear selection", category: "navigation" },
      { key: "n", description: "New task", category: "actions" },
      { key: "l", description: "New list", category: "actions" },
    ]

    if (lists.length > 0) {
      baseShortcuts.push({
        key: "1-9",
        description: "Jump to list",
        category: "navigation",
      })
    }

    return baseShortcuts
  }, [lists.length])

  if (allLists.length === 0) {
    return (
      <AppLayout>
        <Head title="Lists" />
        <EmptyListsState onCreateList={handleNewListClick} />

        <CreateListModal
          open={showNewListModal}
          onOpenChange={setShowNewListModal}
        />
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={[{ label: "All Tasks" }]}>
      <Head title="Lists" />
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-baseline gap-4">
          <h1 className="font-display text-4xl tracking-wide">Vital Signs</h1>
          <div className="text-green-bright flex items-center gap-2 font-mono text-xs tracking-wider uppercase">
            <span className="bg-green-bright h-1.5 w-1.5 animate-pulse rounded-full" />
            Monitoring all lists
          </div>
        </div>
        <Button onClick={() => setShowNewTaskModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Task
        </Button>
      </div>

      <div className="border-border bg-border mb-8 grid grid-cols-4 gap-px overflow-hidden rounded-lg border">
        <VitalCard
          label="Critical"
          value={vitals.critical}
          subtext="dying within 24h"
          variant="critical"
        />
        <VitalCard
          label="Warning"
          value={vitals.warning}
          subtext="dying within 3 days"
          variant="warning"
        />
        <VitalCard
          label="Stable"
          value={vitals.stable}
          subtext="more than 3 days left"
          variant="healthy"
        />
        <VitalCard
          label="Dead"
          value={vitals.graveyard}
          subtext="tasks deceased"
          variant="dead"
        />
      </div>

      <section className="mb-8">
        <h2 className="font-display mb-4 text-xl tracking-wider">Alive</h2>
        {activeTasks.length === 0 ? (
          <p className="text-bone-muted py-8 text-center font-mono text-sm">
            No active tasks — enjoy the calm before the storm
          </p>
        ) : (
          <div className="space-y-2">
            {activeTasks.map((task, index) => (
              <DashboardTaskItem
                key={task.id}
                task={task}
                isSelected={index === selectedIndex}
                onClick={() => setEditingTask(task)}
              />
            ))}
          </div>
        )}
      </section>

      {completedTasks.length > 0 && (
        <section className="border-border from-bg-card to-bg-body mb-8 rounded-lg border-t bg-gradient-to-b px-6 py-6">
          <h2 className="font-display mb-4 text-xl tracking-wider">
            Recently Done
          </h2>

          <div className="space-y-2">
            {completedTasks.map((task, index) => {
              const isSelected = index + activeTasks.length === selectedIndex
              return (
                <div
                  key={task.id}
                  onClick={() => setEditingTask(task)}
                  className={cn(
                    "border-border flex cursor-pointer items-center gap-4 rounded-lg border bg-transparent px-6 py-4 transition-all",
                    isSelected
                      ? "ring-red-bright bg-red-dim/30 border-red-bright/50 opacity-100 ring-2"
                      : "opacity-60 hover:opacity-80",
                  )}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleComplete(task)
                    }}
                    className="border-green bg-green flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-[5px]"
                  >
                    <Check className="h-3 w-3 text-white" />
                  </button>

                  <div className="min-w-0 flex-1">
                    <div className="text-bone decoration-green-bright mb-1 text-[0.95rem] leading-snug font-medium line-through">
                      {task.description}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <span className="border-border bg-bg-hover text-bone-muted rounded border px-2 py-0.5 font-mono text-[0.65rem] tracking-wider uppercase">
                        {task.list.name}
                      </span>
                      <span className="text-bone-muted font-mono text-[0.7rem]">
                        {formatCompletedDate(task.updated_at)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {archivedTasks.length > 0 && (
        <section className="border-border from-bg-body relative rounded-lg border-t bg-gradient-to-b to-[#0a0a0c] px-6 py-8">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="animate-ghost-drift absolute top-0 -left-1/2 h-full w-1/2 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent" />
          </div>

          <div className="relative mb-6">
            <h2 className="font-display text-bone-dim text-xl tracking-wider">
              Recent Deaths
            </h2>
          </div>

          <div className="relative grid grid-cols-3 gap-4">
            {archivedTasks.map((task) => (
              <div
                key={task.id}
                className="rounded-[40px_40px_6px_6px] border border-[#2a2a2e] bg-gradient-to-b from-[#1a1a1d] to-[#141416] px-5 py-6 text-center shadow-lg transition-all hover:-translate-y-1 hover:border-[#3a3a3e] hover:shadow-xl"
              >
                <div className="font-display text-bone-muted mb-3 text-sm tracking-[0.25em]">
                  R.I.P.
                </div>
                <div className="text-bone/70 decoration-red-bright mb-2 truncate text-sm line-through decoration-2">
                  &quot;{task.description}&quot;
                </div>
                <div className="text-bone-dim font-mono text-[0.65rem] tracking-wider">
                  {formatArchivedDate(task.updated_at)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <NewTaskModal
        open={showNewTaskModal}
        onOpenChange={setShowNewTaskModal}
        lists={allLists}
      />

      <CreateListModal
        open={showNewListModal}
        onOpenChange={setShowNewListModal}
      />

      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          listId={editingTask.list.id}
          autoArchiveDays={editingTask.list.auto_archive_days}
        />
      )}

      <DeleteConfirmationModal
        open={!!showDeleteTaskModal}
        onOpenChange={(open) => !open && setShowDeleteTaskModal(null)}
        onConfirm={() =>
          showDeleteTaskModal && handleDeleteTask(showDeleteTaskModal)
        }
        title="Delete Task"
        description={`Are you sure you want to delete "${showDeleteTaskModal?.description}"? This action cannot be undone.`}
        confirmText="Delete Task"
      />

      <KeyboardShortcutsModal
        open={showHelpModal}
        onOpenChange={setShowHelpModal}
        shortcuts={visibleShortcuts}
      />

      <LimitReachedModal
        open={showListLimitModal}
        onOpenChange={setShowListLimitModal}
        title="List Limit Reached"
        description="You've hit the list limit. Nine lists is plenty — any more and you'd need a list of lists."
      />
    </AppLayout>
  )
}
